import * as React from "react";
import {
  DataGrid,
  GridCellModes,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import ExportButton from "../../../components/ExportButton";
import CustomPagination from "../../../components/CustomPagination";
import { ENTITY_TYPES, NOTE_TYPE_VALUE } from "../../../utils";
import { CommonDatagridSx } from "../../../assets/styles/commonSx";
import TableSummary from "../TableSummary";
import { notesApi } from "../../../services/notesApi";
import {
  getDiscountColumns,
  DISCOUNT_EDITABLE_FIELDS,
} from "./discountColumns";
import { getPlanogramColumns } from "./planogramColumns";
import { getOrdersColumns } from "./orderColumns";
import { getItemLevelColumns } from "./itemLevelColumns";
import useStableCallback from "../../../hooks/useStableCallback";

const CustomGridToolbar = React.memo(({ row, preSelections, displayItem }) => {
  return (
    <GridToolbarContainer sx={{ justifyContent: "flex-end" }}>
      <ExportButton
        body={row}
        selections={preSelections}
        displayItem={displayItem}
      />
    </GridToolbarContainer>
  );
});

function DiscountTable({
  row,
  displayItem,
  preSelections,
  getSelectedRows,
  getProcessedRow,
  entityType,
}) {
  const [pageSize, setPageSize] = React.useState(10);
  const [cellModesModel, setCellModesModel] = React.useState({});
  const store = useSelector((store) => store.global.store);
  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.planner,
      storeId: store.id,
    });
  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store.id,
    });

  const plannerNotes = React.useMemo(() => {
    return plannerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
  }, [plannerNotesData]);

  const retailerNotes = React.useMemo(() => {
    const list = retailerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
    return list;
  }, [retailerNotesData]);

  const columns = React.useMemo(() => {
    let baseColumns = [];
    switch (entityType) {
      case ENTITY_TYPES.discount:
        baseColumns = getDiscountColumns(getProcessedRow);
        break;
      case ENTITY_TYPES.planogram:
        baseColumns = getPlanogramColumns(
          getProcessedRow,
          plannerNotes,
          retailerNotes
        );
        break;
      case ENTITY_TYPES.orders:
        baseColumns = getOrdersColumns();
        break;
      default:
        baseColumns = getDiscountColumns(getProcessedRow);
    }
    const baseItemColumns = getItemLevelColumns(getProcessedRow);
    const itemColumns = [...baseColumns];
    switch (entityType) {
      case ENTITY_TYPES.discount:
        itemColumns.splice(5, 0, ...baseItemColumns);
        break;
      case ENTITY_TYPES.planogram:
        itemColumns.splice(9, 0, ...baseItemColumns);
        break;
      default: // do nothing
    }
    return displayItem ? itemColumns : baseColumns;
  }, [displayItem, getProcessedRow, entityType, plannerNotes, retailerNotes]);

  const handleCellClick = React.useCallback((params) => {
    setCellModesModel((prevModel) => {
      if (!params.isEditable) {
        return prevModel;
      }
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: {
            mode: GridCellModes.Edit,
          },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = React.useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleIsCellEditable = useStableCallback((params) => {
    return preSelections.includes(params.row.id);
  });

  const getEditCellClass = useStableCallback((params) => {
    if (DISCOUNT_EDITABLE_FIELDS.includes(params.field) && params.isEditable) {
      return "oso-editable-field";
    }
    return "";
  });

  return (
    <>
      {[ENTITY_TYPES.planogram, ENTITY_TYPES.orders].includes(entityType) && (
        <TableSummary
          rows={row}
          entityType={entityType}
          itemLevel={displayItem}
        />
      )}
      <Box
        sx={{
          display: "flex",
          "& > :not(style)": {
            mx: 1,
            width: "100%",
            height: "100%",
          },
        }}
      >
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            autoHeight
            sx={CommonDatagridSx}
            columns={columns}
            rows={row}
            checkboxSelection
            onSelectionModelChange={(newSelectionModel) => {
              getSelectedRows(newSelectionModel);
            }}
            selectionModel={preSelections}
            isCellEditable={handleIsCellEditable}
            experimentalFeatures={{ newEditingApi: true }}
            processRowUpdate={(newRow) => {
              getProcessedRow(newRow);
              return Promise.resolve(newRow);
            }}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            disableColumnMenu
            disableSelectionOnClick
            hideFooterSelectedRowCount
            cellModesModel={cellModesModel}
            onCellModesModelChange={handleCellModesModelChange}
            onCellClick={handleCellClick}
            components={{
              Pagination: CustomPagination,
              Toolbar: CustomGridToolbar,
            }}
            componentsProps={{
              toolbar: { row, preSelections, displayItem },
            }}
            getCellClassName={getEditCellClass}
          />
        </div>
      </Box>
    </>
  );
}

export default React.memo(DiscountTable);
